import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../../lib/styles/props/component-style-props";
import { CMButtonSearchCondition } from "../CMButtonSearchCondition/CMButtonSearchCondition";
import {
  ComponentSingleStyleConfig,
  useDisclosure,
  useOutsideClick,
  useStyleConfig,
} from "@chakra-ui/react";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { hasValue, isNullish } from "../../../../lib/util/common-util";
import { CMButtonPopupSearchConditionDatalistFree } from "../../button-popup/CMButtonPopupSearchConditionDatalistFree/CMButtonPopupSearchConditionDatalistFree";

export type CMButtonSearchConditionDatalistFreeProps<
  T extends string | number,
> = {
  label: string;

  value?: T | null;

  hideClearButton?: boolean;

  options: { label: string; value: T }[];

  labelSubmit?: string;
  labelClear?: string;

  onChange?: (value: T | null) => void;
} & ComponentStyleProps;

export const CMButtonSearchConditionDatalistFree = <T extends string | number>({
  label,
  value = null,
  hideClearButton = false,

  options,

  labelSubmit,
  labelClear,

  onChange,

  sx,
  ...rest
}: CMButtonSearchConditionDatalistFreeProps<T>) => {
  const styles = useStyleConfig("CMButtonSearchConditionDatalistFreeStyles");

  // 選択値を表示値に変換する
  const displayValue = useMemo(
    () =>
      hasValue(value)
        ? options.find((op) => op.value === value)?.label ?? null
        : null,
    [options, value],
  );

  // 開閉制御
  const {
    isOpen: isOpenPopup,
    onClose: onClosePopup,
    onToggle: onTogglePopup,
  } = useDisclosure();

  const [internalValue, setInternalValue] = useState<T | null>(value);
  const [inputText, setInputText] = useState<string>("");

  const resetAllInternalState = useCallback(
    (newValue: T | null = value) => {
      const newInputText = isNullish(newValue)
        ? ""
        : options.find((op) => op.value === newValue)?.label ?? "";
      setInputText(newInputText);
      setInternalValue(newValue);
    },
    [options, value],
  );

  const handleChangeInputText = useCallback(
    (inputText: string) => {
      setInputText(inputText);

      if (inputText === "") {
        setInternalValue(null);
        return;
      }

      const v = options.find((op) => op.label === inputText)?.value;
      if (isNullish(v)) {
        setInternalValue(null);
        return;
      }

      setInternalValue(v);
    },
    [options],
  );

  /**
   * Submitボタン押下時、呼び出し元に選択値を返却する。
   */
  const handleSubmit = useCallback(() => {
    onClosePopup();
    resetAllInternalState(internalValue);
    onChange?.(internalValue);
  }, [internalValue, onChange, onClosePopup, resetAllInternalState]);

  /**
   * Clearボタン押下時、呼び出し元にnullを返却する。
   */
  const handleClear = useCallback(() => {
    onClosePopup();
    resetAllInternalState(null);
    onChange?.(null);
  }, [onChange, onClosePopup, resetAllInternalState]);

  const ref = useRef(null);
  useOutsideClick({
    ref: ref,
    handler: () => {
      onClosePopup();
      resetAllInternalState();
    },
  });

  return (
    <CMButtonSearchCondition
      label={label}
      displayValue={displayValue}
      isOpenPopup={isOpenPopup}
      onClick={onTogglePopup}
      sx={{ ...styles, ...sx }}
      {...rest}
      ref={ref}
    >
      <CMButtonPopupSearchConditionDatalistFree<T>
        options={options}
        labelSubmit={labelSubmit}
        labelClear={labelClear}
        hideClearButton={hideClearButton}
        inputText={inputText}
        onChangeInputText={handleChangeInputText}
        onSubmit={handleSubmit}
        onClear={handleClear}
      />
    </CMButtonSearchCondition>
  );
};

export const CMButtonSearchConditionDatalistFreeStyles: ComponentSingleStyleConfig =
  {
    baseStyle: {},
  };
